/**
 * PDF Utilities
 * 
 * Provides functions for generating PDF documents from various data sources
 * in the Prelude application.
 */

import jsPDF from 'jspdf';
import i18n from '../i18n/config';

/**
 * Generate a PDF from a table or list of items
 * @param {string} title - The title of the PDF
 * @param {Array} items - Array of items to include in the PDF
 * @param {Array} columns - Column definitions (each with a key and label)
 * @param {Object} options - Additional options for PDF generation
 */
export const generatePDF = async (title, items, columns, options = {}) => {
  const doc = new jsPDF({
    orientation: options.orientation || 'portrait',
    unit: 'mm',
    format: options.format || 'a4'
  });

  // Add title
  doc.setFontSize(18);
  doc.text(title, 14, 20);
  doc.setFontSize(11);
  
  // Add date
  const date = new Date();
  doc.text(`Generated on: ${date.toLocaleDateString()}`, 14, 28);
  
  // Add horizontal line
  doc.setLineWidth(0.5);
  doc.line(14, 32, 196, 32);
  
  // Table settings
  const startY = 40;
  const margin = 14;
  const pageWidth = doc.internal.pageSize.width - 2 * margin;
  const columnWidth = pageWidth / columns.length;
  
  // Add table headers
  doc.setFont('helvetica', 'bold');
  columns.forEach((column, index) => {
    doc.text(column.label, margin + (index * columnWidth), startY);
  });
  
  // Add horizontal line after headers
  doc.line(14, startY + 4, 196, startY + 4);
  
  // Add table rows
  doc.setFont('helvetica', 'normal');
  let y = startY + 12;
  
  items.forEach((item, itemIndex) => {
    // Check if we need a new page
    if (y > 270) {
      doc.addPage();
      y = startY;
      
      // Add headers to new page
      doc.setFont('helvetica', 'bold');
      columns.forEach((column, index) => {
        doc.text(column.label, margin + (index * columnWidth), y);
      });
      
      // Add horizontal line after headers
      doc.line(14, y + 4, 196, y + 4);
      
      doc.setFont('helvetica', 'normal');
      y += 12;
    }
    
    // Add row data
    columns.forEach((column, colIndex) => {
      const value = item[column.key];
      const text = value !== undefined && value !== null ? String(value) : '';
      doc.text(text.substring(0, 30), margin + (colIndex * columnWidth), y);
    });
    
    y += 8;
    
    // Add light gray line between rows (except after the last row)
    if (itemIndex < items.length - 1) {
      doc.setDrawColor(200, 200, 200);
      doc.line(14, y - 4, 196, y - 4);
      doc.setDrawColor(0, 0, 0);
    }
  });
  
  // Save the PDF
  doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
};

/**
 * Generate a PDF from a service object
 * @param {Object} service - The service object
 */
export const generateServicePDF = (service) => {
  const doc = new jsPDF();
  const t = i18n.t; // Get the translation function
  
  // Add title and service info
  doc.setFontSize(18);
  doc.text(service.title || t('services.untitledService', 'Untitled Service'), 14, 20);
  
  doc.setFontSize(12);
  doc.text(`${t('common.date', 'Date')}: ${new Date(service.date).toLocaleDateString(i18n.language)}`, 14, 30);
  
  if (service.time) {
    // Format the time properly for display
    let timeDisplay = '';
    if (typeof service.time === 'string') {
      timeDisplay = service.time;
    } else if (service.time.start) {
      // If it's an ISO string, format it to a readable time
      try {
        const timeDate = new Date(service.time.start);
        timeDisplay = timeDate.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        // If parsing fails, use the original string
        timeDisplay = service.time.start;
      }
    }
    doc.text(`${t('common.time', 'Time')}: ${timeDisplay}`, 14, 38);
  }
  
  if (service.category) {
    doc.text(`${t('services.category', 'Category')}: ${service.category}`, 14, 46);
  }
  
  // Add horizontal line
  doc.setLineWidth(0.5);
  doc.line(14, 50, 196, 50);
  
  // Add songs section
  let y = 60;
  
  if (service.songs && service.songs.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(t('songs.songs', 'Songs'), 14, y);
    y += 10;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    service.songs.forEach((songItem, index) => {
      const song = songItem.song;
      if (!song) return;
      
      doc.text(`${index + 1}. ${song.title}`, 20, y);
      y += 6;
      
      if (song.author) {
        doc.text(`   ${t('songs.author', 'Author')}: ${song.author}`, 20, y);
        y += 6;
      }
      
      if (songItem.key || song.defaultKey) {
        doc.text(`   ${t('songs.key', 'Key')}: ${songItem.key || song.defaultKey}`, 20, y);
        y += 6;
      }
      
      if (song.tempo) {
        doc.text(`   ${t('songs.tempo', 'Tempo')}: ${song.tempo}`, 20, y);
        y += 6;
      }
      
      // Add space between songs
      y += 4;
      
      // Check if we need a new page
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
    });
  }
  
  // Add team section
  if (service.team && service.team.length > 0) {
    // Check if we need a new page
    if (y > 230) {
      doc.addPage();
      y = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(t('services.team', 'Team'), 14, y);
    y += 10;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    service.team.forEach((member) => {
      if (!member.user) return;
      
      const name = `${member.user.firstName} ${member.user.lastName}`;
      const position = member.position === 'Other' ? member.customPosition : member.position;
      // Translate status: Confirmed, Pending, Declined
      let status = member.status;
      if (member.status === 'confirmed') {
        status = t('services.confirmed', 'Confirmed');
      } else if (member.status === 'pending') {
        status = t('services.pending', 'Pending');
      } else if (member.status === 'declined') {
        status = t('services.declined', 'Declined');
      } else {
        status = status.charAt(0).toUpperCase() + status.slice(1);
      }
      
      doc.text(`${name} - ${position} (${status})`, 20, y);
      y += 8;
      
      // Check if we need a new page
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
    });
  }
  
  // Add notes section if available
  if (service.rehearsal && service.rehearsal.notes) {
    // Check if we need a new page
    if (y > 230) {
      doc.addPage();
      y = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(t('services.rehearsalNotes', 'Rehearsal Notes'), 14, y);
    y += 10;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    // Split notes into lines to fit page width
    const splitNotes = doc.splitTextToSize(service.rehearsal.notes, 180);
    doc.text(splitNotes, 14, y);
  }
  
  // Save the PDF
  doc.save(`${service.title.replace(/\s+/g, '_')}_Service.pdf`);
};

/**
 * Generate a PDF from a song object
 * @param {Object} song - The song object
 */
export const generateSongPDF = (song) => {
  const doc = new jsPDF();
  const t = i18n.t; // Get the translation function
  
  // Add title and song info
  doc.setFontSize(18);
  doc.text(song.title || t('songs.untitledSong', 'Untitled Song'), 14, 20);
  
  doc.setFontSize(12);
  
  let y = 30;
  
  if (song.author) {
    doc.text(`${t('songs.author', 'Author')}: ${song.author}`, 14, y);
    y += 8;
  }
  
  if (song.defaultKey) {
    doc.text(`${t('songs.key', 'Key')}: ${song.defaultKey}`, 14, y);
    y += 8;
  }
  
  if (song.tempo) {
    doc.text(`${t('songs.tempo', 'Tempo')}: ${song.tempo}`, 14, y);
    y += 8;
  }
  
  if (song.timeSignature) {
    doc.text(`${t('songs.timeSignature', 'Time Signature')}: ${song.timeSignature}`, 14, y);
    y += 8;
  }
  
  // Add horizontal line
  doc.setLineWidth(0.5);
  doc.line(14, y, 196, y);
  y += 10;
  
  // Add chord chart if available
  if (song.chordChart && song.chordChart.content) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(t('songs.chordChart', 'Chord Chart'), 14, y);
    y += 10;
    
    doc.setFontSize(10);
    doc.setFont('courier', 'normal'); // Monospaced font for chord charts
    
    // Split chord chart into lines
    const lines = song.chordChart.content.split('\n');
    
    lines.forEach((line) => {
      // Check if we need a new page
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      
      doc.text(line, 14, y);
      y += 6;
    });
  }
  
  // Add lyrics if available
  if (song.lyrics && typeof song.lyrics === 'string') {
    // Check if we need a new page
    if (y > 230) {
      doc.addPage();
      y = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(t('songs.lyrics', 'Lyrics'), 14, y);
    y += 10;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    // Split lyrics into lines
    const lyricLines = song.lyrics.split('\n');
    
    lyricLines.forEach((line) => {
      // Check if we need a new page
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      
      doc.text(line, 14, y);
      y += 6;
    });
  }
  
  // Save the PDF
  doc.save(`${song.title.replace(/\s+/g, '_')}_Song.pdf`);
};
